import { loaderApi } from './api';

export interface SatNogsStation {
  id: number;
  name: string;
  lat: number;
  lng: number;
  status?: string;
  last_seen?: string;
}

const STATUSES = ['online', 'testing'] as const;
type Status = (typeof STATUSES)[number];
const CACHE_PREFIX = 'gem:satnogs-stations:v1:';
const FRESH_MS = 60 * 60 * 1000;
const MAX_AGE_MS = 24 * FRESH_MS;
type Snapshot = { fetchedAt: number; stations: SatNogsStation[] };

/** Keep only public station coordinates/identity, never descriptions or user profiles. */
function stationRows(value: unknown): SatNogsStation[] {
  const rows = Array.isArray(value) ? value : (value as { results?: unknown } | null)?.results;
  if (!Array.isArray(rows) || rows.length > 5000) throw new Error('Invalid SatNOGS station directory');
  return rows.flatMap((s) => {
    if (
      !s ||
      !Number.isInteger(s.id) ||
      typeof s.name !== 'string' ||
      !Number.isFinite(s.lat) ||
      Math.abs(s.lat) > 90 ||
      !Number.isFinite(s.lng) ||
      Math.abs(s.lng) > 180
    )
      return [];
    const status = typeof s.status === 'string' ? s.status.toLowerCase() : undefined;
    if (status && !STATUSES.includes(status as Status)) return [];
    return [
      {
        id: s.id,
        name: s.name.slice(0, 200),
        lat: s.lat,
        lng: s.lng,
        status,
        last_seen: typeof s.last_seen === 'string' ? s.last_seen.slice(0, 40) : undefined,
      },
    ];
  });
}

function readSnapshot(status: Status): Snapshot | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + status);
    if (!raw || raw.length > 1024 * 1024) return null;
    const saved = JSON.parse(raw) as Snapshot;
    const age = Date.now() - saved.fetchedAt;
    if (!Number.isFinite(age) || age < 0 || age > MAX_AGE_MS) return null;
    return { fetchedAt: saved.fetchedAt, stations: stationRows(saved.stations) };
  } catch {
    return null;
  }
}

function saveSnapshot(status: Status, saved: Snapshot): void {
  try {
    localStorage.setItem(CACHE_PREFIX + status, JSON.stringify(saved));
  } catch {
    /* A disabled/full browser cache must not block live stations. */
  }
}

/** Independent status feeds: a slow/missing subset must not discard the other one.
 * Cached availability is explicitly dated and is never used beyond one day. */
export async function loadSatNogsStations(signal?: AbortSignal): Promise<{
  stations: SatNogsStation[];
  notice?: string;
  degraded: boolean;
}> {
  signal?.throwIfAborted();
  const results = await Promise.allSettled(
    STATUSES.map(async (status) => {
      const saved = readSnapshot(status);
      const fresh = saved && Date.now() - saved.fetchedAt < FRESH_MS;
      let snapshot = saved;
      let cached = Boolean(fresh);
      let degraded = false;
      if (!fresh) {
        try {
          const response = await loaderApi.feed<unknown>(`satnogs-stations-${status}`, undefined, signal);
          signal?.throwIfAborted();
          snapshot = { stations: stationRows(response), fetchedAt: Date.now() };
          saveSnapshot(status, snapshot);
        } catch (err) {
          signal?.throwIfAborted();
          if (!saved) throw err;
          cached = true;
          degraded = true;
        }
      }
      return { ...snapshot!, cached, degraded };
    }),
  );
  signal?.throwIfAborted();
  if (results.every((r) => r.status === 'rejected'))
    throw new Error(
      'SatNOGS station feeds are unavailable and no recent station directory is cached. Please retry.',
    );

  const notes: string[] = [];
  const byId = new Map<number, SatNogsStation>();
  let degraded = false;
  results.forEach((result, i) => {
    const status = STATUSES[i];
    if (result.status === 'rejected') {
      notes.push(`${status} stations unavailable`);
      degraded = true;
      return;
    }
    const { stations, fetchedAt, cached } = result.value;
    degraded ||= result.value.degraded;
    if (cached)
      notes.push(`${status} stations cached at ${new Date(fetchedAt).toISOString().replace('.000Z', 'Z')}`);
    // Prefer live identity/coordinates if a station changed status since a cached snapshot.
    for (const station of stations) if (!cached || !byId.has(station.id)) byId.set(station.id, station);
  });
  return {
    stations: [...byId.values()],
    degraded,
    notice: notes.length ? `SatNOGS: ${notes.join('; ')}. Station availability may have changed.` : undefined,
  };
}
